/**
 * Feb 2026 — Service Areas section for the PWS Partner landing page.
 *
 * Renders ALL partner `service_suburbs` in the DOM so Googlebot
 * indexes every one (the LocalBusiness JSON-LD `areaServed` mirrors
 * the same list — structured + visible signals must agree).
 *
 * Visual UX: caps to `SERVICE_AREAS_VISIBLE_LIMIT` pills until the
 * user expands. Pages like `pws-tropical-north` (383 suburbs) would
 * otherwise vertically dominate the page. Extras use Tailwind's
 * `hidden` class — Googlebot renders the full DOM regardless, so
 * the SEO surface stays intact.
 *
 * Component is stateful (React `useState` toggle). Extracted from
 * `PwsPartnerWaterTestingPreview.jsx` so we can unit-test the toggle
 * in isolation.
 */
import { useState } from "react";
import { MapPin, ArrowRight } from "lucide-react";

export const SERVICE_AREAS_VISIBLE_LIMIT = 24;

export function ServiceAreasSection({ partner, partnerName, partnerRegion }) {
  const [expanded, setExpanded] = useState(false);
  const suburbs = partner?.service_suburbs || [];
  const overflow = Math.max(0, suburbs.length - SERVICE_AREAS_VISIBLE_LIMIT);
  const hasToggle = overflow > 0;

  return (
    <section
      id="service-areas"
      data-testid="preview-service-areas"
      className="bg-slate-50 py-12 border-t border-slate-200"
      aria-label="Service areas"
    >
      <div className="max-w-5xl mx-auto px-4">
        <div className="max-w-3xl mb-6">
          <h2
            data-testid="preview-service-areas-heading"
            className="text-2xl sm:text-3xl font-bold text-[#0b1f4d]"
          >
            Where we service
          </h2>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            {partnerName} services homeowners across{" "}
            <strong className="text-slate-800">{partnerRegion}</strong> and
            the following suburbs. Kits are posted Australia-wide, but
            these are the areas your local Pristine Water Systems Partner
            covers for in-person support and same-day pickup.
          </p>
        </div>
        <ul
          data-testid="preview-service-areas-list"
          className="flex flex-wrap gap-2"
        >
          {suburbs.map((suburb, i) => {
            const isOverflowItem = i >= SERVICE_AREAS_VISIBLE_LIMIT;
            const isHidden = isOverflowItem && !expanded;
            return (
              <li
                key={suburb}
                data-testid={`preview-service-area-item-${suburb.toLowerCase().replace(/\s+/g, "-")}`}
                data-overflow={isOverflowItem ? "true" : "false"}
                className={
                  "inline-flex items-center gap-1.5 rounded-full border border-slate-300 " +
                  "bg-white px-3 py-1.5 text-xs font-medium text-slate-700 " +
                  (isHidden ? "hidden" : "")
                }
              >
                <MapPin className="w-3 h-3 text-cyan-700" />
                {suburb}
              </li>
            );
          })}
        </ul>
        {hasToggle && (
          <button
            type="button"
            data-testid="preview-service-areas-toggle"
            aria-expanded={expanded}
            aria-controls="service-areas"
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-800 transition-colors"
          >
            {expanded
              ? `Show fewer suburbs`
              : `Show ${overflow} more ${overflow === 1 ? "suburb" : "suburbs"}`}
            <ArrowRight
              className={
                "w-3 h-3 transition-transform " +
                (expanded ? "rotate-90" : "")
              }
            />
          </button>
        )}
        <p className="text-[11px] text-slate-500 mt-4">
          Outside these suburbs? We still post kits across Australia — book
          online and your sample returns to the AWARE lab by prepaid
          express post.
        </p>
      </div>
    </section>
  );
}
